<template>
  <!-- Preloader (shown on initial load) -->
  <AppPreloader v-if="showPreloader" @done="showPreloader = false" />
  <svg id="texture">
    <filter id="noise">
      <feTurbulence
        type="fractalNoise"
        baseFrequency=".9"
        numOctaves="5"
        stitchTiles="stitch"
      ></feTurbulence>
      <feColorMatrix type="saturate" values="0"></feColorMatrix>
    </filter>
    <rect width="100%" height="100%" filter="url(#noise)"></rect>
  </svg>
  <NavbarV2 v-if="!isNotFoundPage && !isResumePage" />

  <router-view />

  <!-- Split Screen Transition Overlays -->
  <div class="transition-curtain top" :class="{ 'is-active': isTransitioning }"></div>
  <div class="transition-curtain bottom" :class="{ 'is-active': isTransitioning }"></div>

  <!-- Transition Title -->
  <div class="transition-title" :class="{ 'is-active': isTransitioning, 'is-scaling': isScaling }">
    <h2 class="transition-title__text">{{ transitionTitle }}</h2>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import NavbarV2 from '@/components/NavbarV2.vue'
import AppPreloader from '@/components/AppPreloaderV2.vue'
// import Navbar from '@/components/Navbar.vue' // Backup - versión original
import Footer from '@/components/Footer.vue'
import { useLenis } from '@/composables/useLenis'
import { useTheme } from '@/composables/useTheme'
import { usePageTransition } from '@/composables/usePageTransition'

const route = useRoute()

// Computed property para detectar si estamos en la página 404
const isNotFoundPage = computed(() => {
  return route.name === 'not-found' || route.path === '/:pathMatch(.*)*'
})

// Preloader state — skip on 404 page
const showPreloader = ref(!isNotFoundPage.value)

// Transition State
const { isTransitioning, isScaling, transitionTitle } = usePageTransition()

// Inicializar sistema de temas
const { initTheme, cleanup: cleanupTheme } = useTheme()

// Inicializar Lenis para smooth scrolling
useLenis()

// Computed property para detectar si estamos en la página de resume
const isResumePage = computed(() => {
  return route.name === 'resume'
})

// Inicializar tema al montar la app
onMounted(() => {
  initTheme()
})

// Limpiar listeners al desmontar
onUnmounted(() => {
  cleanupTheme()
})
</script>

<style>
#texture {
  position: fixed;
  top: 0px;
  left: 0px;
  z-index: 100;
  width: 100%;
  height: calc(100vh);
  opacity: 0.25;
  pointer-events: none;
  transform: translateY(0px);
  /* filter: contrast(120%) brightness(120%); */
  filter: contrast(60%) brightness(50%);
}

/* Transition Curtains */
.transition-curtain {
  @apply fixed left-0 w-full h-[51vh] bg-deep-black z-[9998] pointer-events-none will-change-transform;
  transition: transform 0.5s cubic-bezier(0.87, 0, 0.13, 1);
}

/* When active, they capture clicks */
.transition-curtain.is-active {
  @apply pointer-events-auto;
}

.transition-curtain.top {
  @apply top-0 -translate-y-full;
}

.transition-curtain.bottom {
  @apply bottom-0 translate-y-full;
}

/* Active State: Meet in middle */
.transition-curtain.top.is-active,
.transition-curtain.bottom.is-active {
  @apply translate-y-0;
}

/* Transition Title */
.transition-title {
  @apply fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-100 z-[9999] opacity-0 pointer-events-none will-change-transform;
  /* Delay fade out slightly to 0.2s */
  transition: opacity 0.4s ease 0.2s;
}

.transition-title.is-active {
  @apply opacity-100;
  transition-delay: 0.4s; /* Fade in when curtains are closing */
}

/* Scaling State: Handover (Wait for curtain to open) */
.transition-title.is-scaling {
  /* Stay at 3rem. Opacity is controlled by .is-active now */
  @apply scale-100;
  transform: translate(-50%, -50%) scale(1); /* Explicit transform needed for specificity sometimes, but try @apply first? No, keep explicit transform to be safe or verify if tailwind classes cover it. The @apply above has translate-x/y. */
}

.transition-title__text {
  @apply font-jakarta text-[3rem] font-bold text-text-primary uppercase tracking-[0.2em] text-center whitespace-nowrap;
}
</style>
